/**
 * Zusatzmaterial eines Artikels („Artikel-Kit", VB-26016, ADR 0088).
 *
 * Am Artikel hängt eine Liste echter Artikel mit einer Stückzahl JE EINHEIT des
 * Hauptartikels („4 Schrauben je Kettenrad"). Die Zuordnung ist mehrstufig: ein
 * Zusatzmaterial kann selbst wieder Zusatzmaterial haben. Beim Einfügen des
 * Hauptartikels als Position löst diese Funktion die ganze Kette auf und
 * skaliert die Mengen.
 *
 * Bewusst rein und ohne DB: dieselbe Regel gilt im Materialbedarf, in der
 * Projektauslegung und im Beleg-Editor. Drei Implementierungen wären drei
 * Wahrheiten.
 */

/** Eine Kante des Kit-Graphen: Hauptartikel → Zusatzmaterial mit Menge je Einheit. */
export interface KitEdge {
  parentArticleId: string;
  articleId: string;
  articleNumber: string;
  articleName: string;
  unit: string | null;
  /** Stückzahl je EINER Einheit des Hauptartikels. */
  quantityPerUnit: number;
}

/** Eine aufgelöste Zeile — wird zu einer normalen Position. */
export interface KitLine {
  articleId: string;
  articleNumber: string;
  articleName: string;
  unit: string | null;
  /** Fertig skalierte Menge (Menge des Elternteils × Stückzahl je Einheit). */
  quantity: number;
  /** 1 = direktes Zusatzmaterial des Hauptartikels, 2 = dessen Zusatzmaterial … */
  depth: number;
  /** Artikelnummer des Hauptartikels DIESER Zeile — trägt die Kennzeichnung. */
  parentNumber: string;
}

/** Abgebrochene Kette: ein Artikel taucht in seiner eigenen Kette wieder auf. */
export interface KitCycle {
  articleNumber: string;
  /** Weg bis zum Abbruch, z. B. ['1528.10004', '2200.10001', '1528.10004']. */
  path: string[];
}

export interface KitResolution {
  lines: KitLine[];
  /**
   * Nicht aufgelöste Ketten. Sie werden NICHT stillschweigend übersprungen —
   * der Aufrufer meldet sie (Hausregel, vgl. ADR 0086).
   */
  cycles: KitCycle[];
  /** true, wenn die Tiefengrenze gegriffen hat (Notbremse, kein Normalfall). */
  truncated: boolean;
}

/**
 * Notbremse gegen entartete Ketten. Zehn Stufen sind für Befestigungsmaterial
 * jenseits von allem, was fachlich vorkommt — wer sie erreicht, hat einen
 * Pflegefehler, keinen Anwendungsfall.
 */
export const KIT_MAX_DEPTH = 10;

/**
 * Löst das Zusatzmaterial eines Artikels rekursiv auf.
 *
 * Gleiche Artikel werden bewusst **nicht** zusammengefasst: Steht dieselbe
 * Schraube unter zwei verschiedenen Hauptartikeln, sind das zwei Positionen mit
 * je eigener Herkunft. Eine Summenzeile würde die Kennzeichnung „Zusatzmaterial
 * zu X" verlieren — und genau die ist in der Meldung gefordert.
 */
export function resolveKit(params: {
  rootArticleId: string;
  rootArticleNumber: string;
  /** Menge des Hauptartikels, auf die skaliert wird. */
  quantity: number;
  /** Kanten je Hauptartikel-ID (nur die tatsächlich benötigten Artikel nötig). */
  edgesByParent: ReadonlyMap<string, readonly KitEdge[]>;
}): KitResolution {
  const { rootArticleId, rootArticleNumber, quantity, edgesByParent } = params;
  const lines: KitLine[] = [];
  const cycles: KitCycle[] = [];
  let truncated = false;

  if (!(quantity > 0)) return { lines, cycles, truncated };

  const walk = (
    parentId: string,
    parentNumber: string,
    parentQuantity: number,
    depth: number,
    path: string[],
  ): void => {
    if (depth > KIT_MAX_DEPTH) {
      truncated = true;
      return;
    }
    for (const edge of edgesByParent.get(parentId) ?? []) {
      // Zyklus: der Artikel steht schon im eigenen Weg hierher. Abbrechen und
      // melden — nicht endlos weiterlaufen und nicht stumm weglassen.
      if (path.includes(edge.articleId)) {
        cycles.push({ articleNumber: edge.articleNumber, path: [...path, edge.articleId].map(numberOf) });
        continue;
      }
      const scaled = parentQuantity * edge.quantityPerUnit;
      lines.push({
        articleId: edge.articleId,
        articleNumber: edge.articleNumber,
        articleName: edge.articleName,
        unit: edge.unit,
        quantity: scaled,
        depth,
        parentNumber,
      });
      walk(edge.articleId, edge.articleNumber, scaled, depth + 1, [...path, edge.articleId]);
    }
  };

  /** Artikel-ID → Nummer für die Zyklus-Meldung (nur zur Anzeige). */
  const numbersById = new Map<string, string>([[rootArticleId, rootArticleNumber]]);
  for (const edges of edgesByParent.values()) {
    for (const edge of edges) numbersById.set(edge.articleId, edge.articleNumber);
  }
  function numberOf(articleId: string): string {
    return numbersById.get(articleId) ?? articleId;
  }

  walk(rootArticleId, rootArticleNumber, quantity, 1, [rootArticleId]);
  return { lines, cycles, truncated };
}

/** Alle Artikel-IDs, die zur Auflösung geladen werden müssen (Breitensuche). */
export function collectKitArticleIds(
  rootArticleId: string,
  edgesByParent: ReadonlyMap<string, readonly KitEdge[]>,
): string[] {
  const seen = new Set<string>([rootArticleId]);
  const queue = [rootArticleId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const edge of edgesByParent.get(current) ?? []) {
      if (seen.has(edge.articleId)) continue;
      seen.add(edge.articleId);
      queue.push(edge.articleId);
    }
  }
  seen.delete(rootArticleId);
  return [...seen];
}

const qtyFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });

/**
 * Rückmeldung nach dem Einfügen — nennt auch, was NICHT aufgelöst wurde.
 * Leerer String, wenn es kein Zusatzmaterial gab (dann sagt der Aufrufer nichts).
 */
export function describeKitResult(resolution: KitResolution): string {
  if (resolution.lines.length === 0 && resolution.cycles.length === 0) return '';
  const parts: string[] = [];
  if (resolution.lines.length > 0) {
    parts.push(
      `${resolution.lines.length} ${resolution.lines.length === 1 ? 'Position' : 'Positionen'} Zusatzmaterial ergänzt`,
    );
  }
  if (resolution.cycles.length > 0) {
    const names = [...new Set(resolution.cycles.map((cycle) => cycle.articleNumber))].join(', ');
    parts.push(`${resolution.cycles.length} Kette(n) wegen Ringbezug abgebrochen (${names})`);
  }
  if (resolution.truncated) parts.push(`Verschachtelung tiefer als ${KIT_MAX_DEPTH} Stufen abgeschnitten`);
  return `${parts.join(' · ')}.`;
}

/** Menge lesbar für Meldungen („12,5"). */
export function formatKitQuantity(quantity: number): string {
  return qtyFormat.format(quantity);
}
